if (!customElements.get('hauck-sticky-product-bar')) {
  customElements.define(
    'hauck-sticky-product-bar',
    class HauckStickyProductBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('button[name="add"]');
        this.buttonText = this.button?.querySelector('span');
        this.price = this.querySelector(`#HauckStickyPrice-${this.sectionId}`);

        this.syncFromMainForm();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            this.syncFromVariantEvent(event.data);
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      syncFromMainForm() {
        const mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!mainButton || !this.button) return;

        this.button.disabled = mainButton.disabled;
        if (this.buttonText && mainButton.querySelector('span')) {
          this.buttonText.textContent = mainButton.querySelector('span').textContent.trim();
        }
      }

      syncFromVariantEvent(data) {
        const updatedButton = data.html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const updatedPrice = data.html.getElementById(`price-${this.sectionId}`);

        if (updatedButton && this.button) {
          this.button.disabled = updatedButton.disabled;
          if (this.buttonText && updatedButton.querySelector('span')) {
            this.buttonText.textContent = updatedButton.querySelector('span').textContent.trim();
          }
        }

        if (updatedPrice && this.price) {
          const currentPrice =
            updatedPrice.querySelector('.price-item--sale.price-item--last') ||
            updatedPrice.querySelector('.price__regular .price-item--regular');

          if (currentPrice) this.price.textContent = currentPrice.textContent.trim();
        }
      }
    }
  );
}

if (!customElements.get('hauck-product-features')) {
  customElements.define(
    'hauck-product-features',
    class HauckProductFeatures extends HTMLElement {
      connectedCallback() {
        const dataElement = this.querySelector('[data-hauck-variant-features]');
        if (!dataElement) return;

        try {
          this.featureData = JSON.parse(dataElement.textContent);
        } catch (error) {
          return;
        }

        this.images = Array.from(this.querySelectorAll('[data-feature-index]'));
        this.images.forEach((image) => {
          image.addEventListener('error', () => this.showFallbackImage(image));
        });
        this.updateImages(this.featureData.selectedVariantId);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            this.updateImages(event?.data?.variant?.id);
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      normalizeImageUrl(source) {
        if (!source) return '';
        return String(source).replace('type=webp&', '');
      }

      showFallbackImage(image) {
        const fallbackSource = this.normalizeImageUrl(image.dataset.fallbackSrc);
        if (!fallbackSource || image.getAttribute('src') === fallbackSource) return;
        image.src = fallbackSource;
      }

      updateImages(variantId) {
        if (!variantId || !this.featureData?.variants) return;
        const variantImages = this.featureData.variants[String(variantId)];
        if (!Array.isArray(variantImages)) return;

        this.images.forEach((image) => {
          const index = Number(image.dataset.featureIndex);
          const nextSource = this.normalizeImageUrl(variantImages[index] || image.dataset.fallbackSrc);
          if (!nextSource || image.getAttribute('src') === nextSource) return;
          image.src = nextSource;
        });
      }
    }
  );
}

if (!customElements.get('hauck-coupon')) {
  customElements.define(
    'hauck-coupon',
    class HauckCoupon extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.status = this.querySelector('[data-hauck-coupon-status]');
        this.price = this.querySelector('[data-hauck-coupon-price]');
        this.saving = this.querySelector('[data-hauck-coupon-saving]');

        try {
          this.prices = JSON.parse(this.querySelector('[data-hauck-coupon-prices]')?.textContent || '{}');
        } catch (error) {
          this.prices = {};
        }

        this.button?.addEventListener('click', () => this.copyCode());
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId) return;
            this.updatePrice(event.data.variant?.id);
          });
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      updatePrice(variantId) {
        const values = this.prices[String(variantId)];
        if (!values) return;
        if (this.price) this.price.textContent = values.price;
        if (this.saving) this.saving.textContent = values.saving;
      }

      async copyCode() {
        try {
          await navigator.clipboard.writeText(this.dataset.code);
        } catch (error) {
          const input = document.createElement('textarea');
          input.value = this.dataset.code;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }

        this.classList.add('is-copied');
        if (this.status) this.status.textContent = `Coupon code ${this.dataset.code} copied`;
        window.setTimeout(() => this.classList.remove('is-copied'), 1800);
      }
    }
  );
}

if (!customElements.get('hauck-delivery-estimate')) {
  customElements.define(
    'hauck-delivery-estimate',
    class HauckDeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const timeZone = this.dataset.timeZone || 'Asia/Dubai';
        const now = new Date();
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hourCycle: 'h23',
        }).formatToParts(now);
        const value = (type) => Number(parts.find((part) => part.type === type)?.value);
        const dubaiDate = new Date(Date.UTC(value('year'), value('month') - 1, value('day')));
        const deliveryDays = value('hour') < 17 ? 1 : 2;
        const deliveryDate = new Date(dubaiDate.getTime() + deliveryDays * 86400000);
        const shortDate = new Intl.DateTimeFormat('en-AE', {
          timeZone: 'UTC',
          month: 'short',
          day: 'numeric',
        });
        const longDate = new Intl.DateTimeFormat('en-AE', {
          timeZone: 'UTC',
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });

        const deliveryTarget = this.querySelector('[data-hauck-delivery-date]');
        const processingTarget = this.querySelector('[data-hauck-processing-date]');
        const arrivalTarget = this.querySelector('[data-hauck-arrival-date]');
        if (deliveryTarget) deliveryTarget.textContent = longDate.format(deliveryDate);
        if (processingTarget) processingTarget.textContent = shortDate.format(dubaiDate);
        if (arrivalTarget) arrivalTarget.textContent = shortDate.format(deliveryDate);
      }
    }
  );
}
